/**
 * Definiciones de proceso como datos (ADR 0005). Todos los valores son supuestos ilustrativos.
 *
 * - P0 y P1: procesos mínimos de prueba, calculables a mano (doc. 05 §2 y caso M5).
 * - Posventa 1.0: versión simplificada del proceso de referencia (doc. 04 §4.5) para la rebanada
 *   vertical 1.0. Rigen P1 (control de calidad administrativo, hecho por el mismo técnico) y P2
 *   (promesa en horas hábiles). Los ajustes del §4.6 llegan en el paso 1.2.
 */

import type { Distribucion } from '../nucleo/aleatoriedad/distribuciones';
import type { DefinicionDeProceso, Etapa } from '../nucleo/dominio/proceso';
import { supuesto } from '../nucleo/dominio/parametros';

const constante = (valor: number): Distribucion => ({ tipo: 'constante', valor });
const triangular = (min: number, tipico: number, max: number): Distribucion => ({
  tipo: 'triangular',
  min,
  tipico,
  max,
});

const SALIDA: Etapa = {
  id: 'salida',
  nombre: 'Salida del taller',
  tipo: 'salida',
  categoria: 'traslado',
  obligatoria: true,
  lugar: 'salida',
  personas: [],
  hitoAlIniciar: 'fin',
};

/** P0: recepción → servicio → lavado → salida, duraciones constantes (doc. 05 §2). */
export function procesoP0(): DefinicionDeProceso {
  return {
    id: 'prueba-p0',
    nombre: 'Proceso de prueba P0',
    version: 1,
    tiposDeUnidad: [
      {
        id: 'mantenimiento',
        nombre: 'Mantenimiento',
        promesaMin: supuesto(120, 'min'),
        horasVendidas: supuesto(1, 'h'),
        bahias: ['mixto', 'fast-lane'],
      },
    ],
    atributos: [],
    etapaInicial: 'recepcion',
    etapas: [
      {
        id: 'recepcion',
        nombre: 'Recepción con asesor',
        tipo: 'actividad',
        categoria: 'administrativa',
        obligatoria: true,
        lugar: 'recepcion',
        personas: [{ rol: 'asesor' }],
        duracion: { porTipo: { '*': constante(15) }, origen: 'supuesto' },
        hitoAlTerminar: 'apertura-ot',
      },
      {
        id: 'servicio',
        nombre: 'Servicio',
        tipo: 'actividad',
        categoria: 'valor-agregado',
        obligatoria: true,
        aplicaA: ['mantenimiento'],
        lugar: 'bahia',
        personas: [{ rol: 'tecnico' }],
        duracion: { porTipo: { '*': constante(60) }, origen: 'supuesto' },
        hitoAlTerminar: 'trabajo-terminado',
      },
      {
        id: 'lavado',
        nombre: 'Lavado',
        tipo: 'actividad',
        categoria: 'valor-agregado',
        obligatoria: true,
        lugar: 'lavado',
        personas: [],
        duracion: { porTipo: { '*': constante(20) }, origen: 'supuesto' },
        hitoAlTerminar: 'listo',
      },
      SALIDA,
    ],
    transiciones: [
      { de: 'recepcion', a: 'servicio', sino: true },
      { de: 'servicio', a: 'lavado', sino: true },
      { de: 'lavado', a: 'salida', sino: true },
    ],
  };
}

/** P0 con espera de repuesto a mitad del servicio, reteniendo bahía y técnico (caso M6). */
export function procesoP0ConRepuesto(
  probabilidad = 1,
  esperaMin = 25,
  fraccion = 0.5,
): DefinicionDeProceso {
  const p = procesoP0();
  p.id = 'prueba-p0-repuesto';
  p.nombre = 'Proceso de prueba P0 con repuesto';
  p.atributos = [
    {
      id: 'requiereRepuesto',
      nombre: 'Requiere repuesto',
      probabilidad: supuesto({ mantenimiento: probabilidad }, ''),
    },
  ];
  const servicio = p.etapas.find((e) => e.id === 'servicio')!;
  servicio.interrupcion = {
    si: { atributo: 'requiereRepuesto', igual: true },
    fraccion: constante(fraccion),
    etapa: 'espera-repuesto',
  };
  p.etapas.splice(2, 0, {
    id: 'espera-repuesto',
    nombre: 'Espera de repuesto',
    tipo: 'espera-externa',
    categoria: 'espera',
    obligatoria: false,
    lugar: 'actual',
    personas: [],
    duracion: { porTipo: { '*': constante(esperaMin) }, origen: 'supuesto' },
  });
  return p;
}

/** P1 = P0 + diagnóstico, aprobación y reparación para «otros» (caso M5). */
export function procesoP1(): DefinicionDeProceso {
  const p = procesoP0();
  p.id = 'prueba-p1';
  p.nombre = 'Proceso de prueba P1';
  p.tiposDeUnidad.push({
    id: 'otros',
    nombre: 'Otros servicios',
    promesaMin: supuesto(240, 'min'),
    horasVendidas: supuesto(2, 'h'),
    bahias: ['mixto', 'mayor'],
  });
  const [recepcion, servicio, lavado, salida] = p.etapas as [Etapa, Etapa, Etapa, Etapa];
  p.etapas = [
    recepcion,
    {
      id: 'diagnostico',
      nombre: 'Diagnóstico',
      tipo: 'actividad',
      categoria: 'valor-agregado',
      obligatoria: false,
      lugar: 'bahia',
      personas: [{ rol: 'tecnico' }],
      duracion: { porTipo: { '*': constante(30) }, origen: 'supuesto' },
    },
    {
      id: 'aprobacion',
      nombre: 'Aprobación del cliente',
      tipo: 'espera-externa',
      categoria: 'espera-cliente',
      obligatoria: false,
      lugar: 'actual',
      personas: [],
      duracion: { porTipo: { '*': constante(45) }, origen: 'supuesto' },
    },
    servicio,
    {
      id: 'reparacion',
      nombre: 'Reparación',
      tipo: 'actividad',
      categoria: 'valor-agregado',
      obligatoria: false,
      lugar: 'bahia',
      personas: [{ rol: 'tecnico' }],
      duracion: { porTipo: { '*': constante(90) }, origen: 'supuesto' },
      hitoAlTerminar: 'trabajo-terminado',
    },
    lavado,
    salida,
  ];
  p.transiciones = [
    { de: 'recepcion', a: 'diagnostico', si: { tipoUnidad: 'otros' } },
    { de: 'recepcion', a: 'servicio', sino: true },
    { de: 'diagnostico', a: 'aprobacion', sino: true },
    { de: 'aprobacion', a: 'reparacion', sino: true },
    { de: 'servicio', a: 'lavado', sino: true },
    { de: 'reparacion', a: 'lavado', sino: true },
    { de: 'lavado', a: 'salida', sino: true },
  ];
  return p;
}

/**
 * Proceso de posventa de la rebanada 1.0 (supuestos ilustrativos, editables).
 * Rutas: mantenimiento = recepción → servicio → control de calidad → lavado → espera de retiro →
 * salida; otros = recepción → diagnóstico → aprobación → (aprueba: reparación → control de calidad)
 * → lavado → espera de retiro → salida.
 */
export function procesoPosventa(): DefinicionDeProceso {
  const repuesto = {
    si: { atributo: 'requiereRepuesto', igual: true },
    fraccion: { tipo: 'uniforme', min: 0.25, max: 0.65 } as Distribucion,
    etapa: 'espera-repuesto',
  };
  return {
    id: 'posventa-referencia',
    nombre: 'Posventa automotriz (referencia 1.0)',
    version: 1,
    tiposDeUnidad: [
      {
        id: 'mantenimiento',
        nombre: 'Mantenimiento',
        promesaMin: supuesto(180, 'min'),
        horasVendidas: supuesto(1.5, 'h'),
        bahias: ['mixto', 'fast-lane'],
      },
      {
        id: 'otros',
        nombre: 'Otros servicios',
        promesaMin: supuesto(360, 'min'),
        horasVendidas: supuesto(3, 'h'),
        bahias: ['mixto', 'mayor'],
      },
    ],
    atributos: [
      {
        id: 'requiereRepuesto',
        nombre: 'Requiere repuesto',
        probabilidad: supuesto({ mantenimiento: 0.1, otros: 0.25 }, ''),
      },
      {
        id: 'apruebaReparacion',
        nombre: 'Aprueba la reparación',
        probabilidad: supuesto({ otros: 0.9 }, ''),
      },
    ],
    etapaInicial: 'recepcion',
    etapas: [
      {
        id: 'recepcion',
        nombre: 'Recepción con asesor',
        tipo: 'actividad',
        categoria: 'administrativa',
        obligatoria: true,
        lugar: 'recepcion',
        personas: [{ rol: 'asesor' }],
        duracion: { porTipo: { '*': triangular(10, 15, 25) }, origen: 'supuesto' },
        hitoAlTerminar: 'apertura-ot',
      },
      {
        id: 'diagnostico',
        nombre: 'Diagnóstico',
        tipo: 'actividad',
        categoria: 'valor-agregado',
        obligatoria: false,
        lugar: 'bahia',
        lugarDeEspera: 'patio',
        personas: [{ rol: 'tecnico' }],
        duracion: { porTipo: { '*': triangular(25, 40, 70) }, origen: 'supuesto' },
      },
      {
        id: 'aprobacion',
        nombre: 'Aprobación del cliente',
        tipo: 'espera-externa',
        categoria: 'espera-cliente',
        obligatoria: false,
        lugar: 'actual',
        personas: [],
        duracion: { porTipo: { '*': triangular(15, 45, 120) }, origen: 'supuesto' },
        ventanaHabil: { inicio: '08:00', fin: '18:00' },
      },
      {
        id: 'servicio',
        nombre: 'Servicio de mantenimiento',
        tipo: 'actividad',
        categoria: 'valor-agregado',
        obligatoria: true,
        aplicaA: ['mantenimiento'],
        lugar: 'bahia',
        lugarDeEspera: 'patio',
        personas: [{ rol: 'tecnico', retenerHasta: 'control-calidad' }],
        duracion: { porTipo: { '*': triangular(60, 90, 140) }, origen: 'supuesto' },
        interrupcion: repuesto,
        hitoAlTerminar: 'trabajo-terminado',
      },
      {
        id: 'reparacion',
        nombre: 'Reparación',
        tipo: 'actividad',
        categoria: 'valor-agregado',
        obligatoria: false,
        lugar: 'bahia',
        personas: [{ rol: 'tecnico', retenerHasta: 'control-calidad' }],
        duracion: { porTipo: { '*': triangular(90, 150, 300) }, origen: 'supuesto' },
        interrupcion: repuesto,
        hitoAlTerminar: 'trabajo-terminado',
      },
      {
        id: 'espera-repuesto',
        nombre: 'Espera de repuesto',
        tipo: 'espera-externa',
        categoria: 'espera',
        obligatoria: false,
        lugar: 'actual',
        personas: [],
        duracion: { porTipo: { '*': triangular(10, 25, 60) }, origen: 'supuesto' },
      },
      {
        id: 'control-calidad',
        nombre: 'Control de calidad',
        tipo: 'actividad',
        categoria: 'administrativa',
        obligatoria: true,
        aplicaA: ['mantenimiento'],
        lugar: 'actual',
        personas: [{ rol: 'tecnico', deLaUnidad: true }],
        duracion: { porTipo: { '*': triangular(5, 10, 20) }, origen: 'supuesto' },
      },
      {
        id: 'lavado',
        nombre: 'Lavado',
        tipo: 'actividad',
        categoria: 'valor-agregado',
        obligatoria: true,
        lugar: 'lavado',
        lugarDeEspera: 'patio',
        personas: [],
        duracion: { porTipo: { '*': triangular(15, 20, 30) }, origen: 'supuesto' },
        hitoAlTerminar: 'listo',
      },
      {
        id: 'espera-retiro',
        nombre: 'Espera de retiro del cliente',
        tipo: 'espera-externa',
        categoria: 'espera-cliente',
        obligatoria: true,
        lugar: 'entrega',
        lugarDeEspera: 'patio',
        personas: [],
        duracion: { porTipo: { '*': triangular(20, 60, 180) }, origen: 'supuesto' },
        ventanaHabil: { inicio: '08:00', fin: '19:00' },
      },
      SALIDA,
    ],
    transiciones: [
      { de: 'recepcion', a: 'diagnostico', si: { tipoUnidad: 'otros' } },
      { de: 'recepcion', a: 'servicio', sino: true },
      { de: 'diagnostico', a: 'aprobacion', sino: true },
      { de: 'aprobacion', a: 'reparacion', si: { atributo: 'apruebaReparacion', igual: true } },
      { de: 'aprobacion', a: 'lavado', sino: true },
      { de: 'servicio', a: 'control-calidad', sino: true },
      { de: 'reparacion', a: 'control-calidad', sino: true },
      { de: 'control-calidad', a: 'lavado', sino: true },
      { de: 'lavado', a: 'espera-retiro', sino: true },
      { de: 'espera-retiro', a: 'salida', sino: true },
    ],
  };
}
